/**
 * Burton Forms — `<burton-validate>` generic field wrapper + exported base class (Layer 3).
 *
 * Wraps existing light-DOM control(s) (one control or one same-named radio/checkbox group) and adds
 * validation — it does NOT render them. The wrapped `<input type>`/`<select>`/`<textarea>` is the
 * source of truth, so there's no `type` attribute and no per-type element; controls are
 * auto-discovered (`data-burton-validate-el="input"` disambiguates a control it should not bind).
 * Enhancers such as <burton-subdivision-options> extend the exported base
 * `window.BAO.Forms.Validate`.
 *
 *   <burton-validate rules="required|email">
 *     <label for="email">Email</label>
 *     <input type="email" id="email" name="email" required>
 *   </burton-validate>
 */
class BurtonValidate extends window.BAO.CustomElement() {
  // BAO calls this once (guarded by the base connectedCallback) — our init + listener wiring.
  setupListeners () {
    super.setupListeners()

    this.ruleTokens = (this.getAttribute('rules') || '')
      .split('|')
      .map((token) => token.trim())
      .filter(Boolean)
    this.customValidators = []
    this.validity = { state: 'pending' }
    this.errorId = `bfrm-Field-error-${window.BAO.Forms.nextId()}`
    this.form = this.closest('burton-form')

    this.controls = this.resolveControls()

    if (this.controls.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        '[BAO.Forms] <burton-validate> found no input/select/textarea to wrap.',
        this,
      )

      return
    }

    // Hooks stay field-named (CSS only) and are added here so authors don't hand-add them to markup.
    this.classList.add('bfrm-Field')
    this.controls.forEach((control) =>
      control.classList.add('bfrm-Field_Input'),
    )

    this.renderErrorContainer()

    if (this.form) {
      if (typeof this.form.registerField === 'function') {
        this.form.registerField(this)
      } else {
        customElements.whenDefined('burton-form').then(() => {
          if (this.form && typeof this.form.registerField === 'function') {
            this.form.registerField(this)
          }
        })
      }
    }

    this.controls.forEach((control) => {
      this.listeners.add(control, 'blur', () => {
        this.trimControl(control)
        this.validate()
      })
      this.listeners.add(control, 'input', () => this.clearError())
      this.listeners.add(control, 'change', () => this.validate())
    })
  }

  /**
   * Auto-discover wrapped control(s); plural so radio/checkbox groups bind every member. Marker wins.
   *
   * @returns {HTMLElement[]}
   */
  resolveControls () {
    const marked = [
      ...this.querySelectorAll('[data-burton-validate-el="input"]'),
    ]

    return marked.length
      ? marked
      : [...this.querySelectorAll('input, select, textarea')]
  }

  /**
   * @returns {HTMLElement}
   */
  get inputEl () {
    return this.controls ? this.controls[0] : null
  }

  /**
   * Derived from the wrapped control's `name` (single source of truth) so the submitted name and the
   * key the coordinator routes server errors by always match. A group shares one name.
   *
   * @returns {string}
   */
  get fieldName () {
    const control = this.controls ? this.controls[0] : null
    return control && control.name ? control.name : ''
  }

  /**
   * App escape hatch: attach a custom (optionally async) validator run on blur/submit.
   *
   * @param {Function} fn
   */
  addValidator (fn) {
    this.customValidators = this.customValidators || []
    this.customValidators.push(fn)
  }

  /**
   * Strip surrounding whitespace from a text-like control, in place.
   * @param {HTMLElement} control
   */
  trimControl (control) {
    const trimmable = [
      'text',
      'search',
      'tel',
      'url',
      'email',
      'number',
      'textarea',
    ]
    const type = control.tagName === 'TEXTAREA' ? 'textarea' : control.type

    if (trimmable.indexOf(type) === -1) return
    if (typeof control.value !== 'string') return

    const trimmed = control.value.trim()

    // Only assign on change — writing `.value` moves the caret and dirties the field for no reason.
    if (trimmed !== control.value) control.value = trimmed
  }

  /**
   * Read current value: file → count (empty string when there are none); radio/checkbox group →
   * checked value(s); else → `.value`, with surrounding whitespace ignored.
   *
   * Trimming here as well as on blur is deliberate: blur normalizes what gets SUBMITTED, this
   * normalizes what gets TESTED.
   *
   * @returns {string}
   */
  readValue () {
    const controls = this.controls || []

    if (controls.length === 0) return ''

    const first = controls[0]

    if (first.type === 'file') {
      const count = first.files ? first.files.length : 0

      return count === 0 ? '' : String(count)
    }

    if (first.type === 'checkbox' || first.type === 'radio') {
      return controls
        .filter((control) => control.checked)
        .map((control) => control.value || 'on')
        .join(',')
    }

    if (first.value == null) return ''

    return typeof first.value === 'string' ? first.value.trim() : first.value
  }

  /**
   * @param {string[]} params
   * @returns {Object} ctx passed to validators.
   */
  context (params = []) {
    return {
      country: this.form ? this.form.country : null, // primary key for postal/phone lookups
      region: this.form ? this.form.region : null, // derived bucket, for region-level rules
      form: this.form,
      name: this.fieldName,
      field: this,
      params,
    }
  }

  /**
   * Resolve the message for a failed rule, most specific first:
   *
   *   1. the field's `data-error-<rule>` attribute — an explicit author instruction, so it wins;
   *   2. a string returned by the rule's own `test`, for a rule that reports several distinct
   *      failure reasons;
   *   3. the rule's `message`, resolved for the form's locale.
   *
   * @param {string} ruleName
   * @param {Object} rule
   * @param {Object} ctx
   * @param {*} [result]  whatever `test` returned; used only when it's a string
   * @returns {string}
   */
  messageFor (ruleName, rule, ctx, result) {
    const override = this.getAttribute(`data-error-${ruleName.toLowerCase()}`)

    if (override) return override
    if (typeof result === 'string') return result

    return resolveMessage(
      rule.message,
      ctx,
      this.form ? this.form.locale : null,
    )
  }

  /**
   * Whether this field currently participates in validation + form aggregation. An inactive field
   * is skipped entirely (never blocks submit, never reports invalid).
   *
   * Default signal is visibility (a field hidden via `display:none` on itself or an ancestor is
   * inactive). `data-burton-active="true|false"` is an explicit override for the rare case where
   * visibility is not the right signal (always-in-DOM-but-optional fields, etc.).
   *
   * @returns {boolean}
   */
  isActive () {
    const override = this.getAttribute('data-burton-active')

    if (override === 'true') return true
    if (override === 'false') return false

    return this.isVisible()
  }

  /**
   * `checkVisibility()` (default options) is false when the element or an ancestor is `display:none`
   * or `content-visibility:hidden` — exactly the sections a step hides — and ignores
   * `visibility:hidden`/`opacity`. Falls back to `offsetParent` where unsupported.
   *
   * @returns {boolean}
   */
  isVisible () {
    if (typeof this.checkVisibility === 'function') {
      return this.checkVisibility()
    }

    return this.offsetParent !== null
  }

  /**
   * Synchronous pass/fail, no DOM side effects (used for live gating). Skips async validators.
   *
   * @returns {boolean}
   */
  check () {
    if (!this.isActive()) return true

    const value = this.readValue()

    for (const token of this.ruleTokens) {
      const [ruleName, ...params] = token.split(':')
      const rule = window.BAO.Forms.rules.get(ruleName)

      if (!rule) return false

      const result = rule.test(value, this.context(params))

      if (result instanceof Promise) continue
      if (result !== true) return false
    }

    for (const validator of this.customValidators) {
      const result = validator(value, this.context())

      if (result instanceof Promise) continue
      if (result !== true) return false
    }

    return true
  }

  /**
   * Full validation with rendering + a11y + notification. Runs named rules then custom validators.
   *
   * @returns {Promise<boolean>}
   */
  async validate () {
    if (!this.isActive()) {
      this.hideError()
      this.validity = { state: 'valid' }
      this.notifyValidity()

      return true
    }

    const value = this.readValue()

    for (const token of this.ruleTokens) {
      const [ruleName, ...params] = token.split(':')
      const rule = window.BAO.Forms.rules.get(ruleName)

      if (!rule) {
        // eslint-disable-next-line no-console
        console.error(
          `[BAO.Forms] field "${this.fieldName}" references unknown rule "${ruleName}".`,
        )
        this.setInvalid('Configuration error')

        return false
      }

      const ctx = this.context(params)
      const result = await rule.test(value, ctx)

      if (result !== true) {
        this.setInvalid(this.messageFor(ruleName, rule, ctx, result))

        return false
      }
    }

    for (const validator of this.customValidators) {
      this.setPending()

      const result = await validator(value, this.context())

      if (result !== true) {
        this.setInvalid(typeof result === 'string' ? result : 'Invalid')

        return false
      }
    }

    this.setValid()

    return true
  }

  /**
   * Create the empty, persistent live region and wire aria-describedby (fieldset for a group, else
   * each control). Must exist before any error so announcements fire.
   */
  renderErrorContainer () {
    if (this.errorEl) return

    const errorEl = document.createElement('span')

    errorEl.className = 'bfrm-Field_Error'
    errorEl.id = this.errorId
    errorEl.setAttribute('role', 'status')
    errorEl.setAttribute('aria-live', 'polite')

    this.appendChild(errorEl)
    this.errorEl = errorEl

    this.describedTargets().forEach((target) => {
      const existing = (target.getAttribute('aria-describedby') || '')
        .split(' ')
        .filter(Boolean)

      if (!existing.includes(this.errorId)) existing.push(this.errorId)

      target.setAttribute('aria-describedby', existing.join(' '))
    })
  }

  /**
   * @returns {HTMLElement[]}
   */
  describedTargets () {
    const fieldset = this.querySelector('fieldset')

    return fieldset ? [fieldset] : this.controls
  }

  /**
   * @param {string} message
   */
  setInvalid (message) {
    this.validity = { state: 'invalid', message }

    if (this.errorEl) {
      this.errorEl.textContent = message
    }

    this.controls.forEach((control) =>
      control.setAttribute('aria-invalid', 'true'),
    )
    this.classList.add('bfrm-Field-invalid')
    this.notifyValidity()
  }

  setValid () {
    this.validity = { state: 'valid' }
    this.hideError()
    this.notifyValidity()
  }

  setPending () {
    this.validity = { state: 'pending' }
    this.notifyValidity()
  }

  // Clear the visible error without asserting validity (while typing); recomputed on next blur/change.
  clearError () {
    this.hideError()
  }

  hideError () {
    if (this.errorEl) {
      this.errorEl.textContent = ''
    }

    this.controls.forEach((control) => control.removeAttribute('aria-invalid'))
    this.classList.remove('bfrm-Field-invalid')
  }

  notifyValidity () {
    this.dispatchEvent(
      new CustomEvent('burton:field-validity', {
        bubbles: true,
        detail: { field: this },
      }),
    )
  }
}

/**
 * Candidate message keys for a locale, most specific first: exact locale, then bare language, then `en`.
 *
 * Keys are lowercased on both sides, so a table may be written `'fr-CA'` and a form may pass
 * `fr-ca` — or vice versa.
 *
 * @param {string|null} [locale]  e.g. "fr-CA", "fr", or null
 * @returns {string[]}
 */
function messageKeysFor (locale) {
  const normalized = (locale || 'en').toLowerCase()
  const language = normalized.split('-')[0]
  const candidates = [normalized]

  if (language !== normalized) candidates.push(language)
  if (!candidates.includes('en')) candidates.push('en')

  return candidates
}

/**
 * @param {string|((ctx: Object) => string)|Object.<string,string>} message  string, fn, or locale-keyed object
 * @param {Object} ctx
 * @param {string|null} [locale]  form locale, used to pick a locale-keyed message (`en` fallback)
 * @returns {string}
 */
function resolveMessage (message, ctx, locale) {
  if (typeof message === 'function') return message(ctx)

  if (message && typeof message === 'object') {
    const table = {}

    Object.keys(message).forEach((key) => {
      table[key.toLowerCase()] = message[key]
    })

    const match = messageKeysFor(locale).find((key) => table[key])
    const localized = match ? table[match] : ''

    return typeof localized === 'function' ? localized(ctx) : localized || ''
  }

  return message || ''
}

if (!customElements.get('burton-validate')) {
  customElements.define('burton-validate', BurtonValidate)
}

window.BAO.Forms.Validate = window.BAO.Forms.Validate || BurtonValidate
